import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { makeStyles } from 'tss-react/mui';

import { authSelectors } from 'app-components/flux/auth';
import { getEnvironmentConfig } from 'app-components/utils/core';
import StdDialog from 'components/Dialogs/StdDialog';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';
import AxiosFactory from 'utils/axiosFactory';

const useStyles = makeStyles()(() => ({
  dense: {
    padding: '0 !important',
    margin: '0 !important',
    minHeight: '0 !important',
  },
  expansionPanelSummary: {
    flexDirection: 'row-reverse',
    padding: '0 !important',
    margin: '0 !important',
    minHeight: '0 !important',
  },
}));

const RequestDialog = (props) => {
  const { classes } = useStyles();
  const { path, query, onHandleClose, repeat, delay, ...otherProps } = props;
  const [method, setMethod] = useState(props.method || 'POST');
  const [url, setURL] = useState(props.url || `${getEnvironmentConfig().services_url}`);
  const [body, setBody] = useState(props.body);
  const [response, setResponse] = useState();
  const [count, setCount] = useState(repeat ?? 1);
  const [pause, setPause] = useState(delay ?? 0);
  const [intervalId, setIntervalId] = useState();
  const [current, setCurrent] = useState();
  const datasetId = useSelector(authSelectors.getDatasetId);

  useEffect(() => {
    setMethod(props.method);
  }, [props.method]);
  useEffect(() => {
    setBody(props.body);
  }, [props.body]);

  useEffect(() => {
    const customURL = new URL(
      props.url || path || '',
      props.url ? undefined : `${getEnvironmentConfig().services_url}`,
    );
    if (query) {
      Object.entries(query).forEach(([key, value]) => customURL.searchParams.append(key, value));
    }
    setURL(customURL.href);
  }, [path, query, props.url]);

  const sendRequest = useCallback(() => {
    let n = 0;
    setCurrent(n);
    const interval = setInterval(() => {
      dispatchSimpleNotification('request sent');
      setResponse(undefined);
      const qcsAxios = AxiosFactory.get('qcs');
      switch (method) {
        case 'POST':
          qcsAxios
            .post(url, body, { headers: { 'qcs-dataset-id': datasetId || null } })
            .then((axiosResponse) => setResponse(axiosResponse))
            .catch((error) => setResponse({ status: error.message, ...error.response }));
          break;
        case 'PUT':
          qcsAxios
            .put(url, body, { headers: { 'qcs-dataset-id': datasetId || null } })
            .then((axiosResponse) => setResponse(axiosResponse))
            .catch((error) => setResponse({ status: error.message, ...error.response }));
          break;
        case 'DELETE':
          qcsAxios
            .delete(url, { headers: { 'qcs-dataset-id': datasetId || null } })
            .then((axiosResponse) => setResponse(axiosResponse))
            .catch((error) => setResponse({ status: error.message, ...error.response }));
          break;
        case 'GET':
        default:
          qcsAxios
            .get(url, { headers: { 'qcs-dataset-id': datasetId || null } })
            .then((axiosResponse) => setResponse(axiosResponse))
            .catch((error) => setResponse({ status: error.message, ...error.response }));
      }
      n += 1;
      setCurrent(n);
      if (n >= count) {
        clearInterval(interval);
        setIntervalId(undefined);
        setCurrent(undefined);
      }
    }, pause);
    setIntervalId(interval);
  }, [method, url, body, count, pause, setIntervalId, setCurrent, datasetId]);

  return (
    <StdDialog
      onClose={(event, reason) => {
        if (reason !== 'ENTER_KEY_DOWN') onHandleClose(event, reason);
      }}
      size='sm'
      title='Send Request'
      {...otherProps}
    >
      <DialogContent>
        <Box display='flex'>
          <TextField
            onChange={(event) => setMethod(event.target.value)}
            value={method}
            label='Method'
            variant='filled'
            style={{ width: 200 }}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') {
                sendRequest();
              }
            }}
          />
          <TextField
            autoFocus
            onFocus={(event) => event.target.select()}
            onChange={(event) => setURL(event.target.value)}
            value={url}
            fullWidth
            label='URL'
            variant='filled'
            style={{ width: 800 }}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') {
                sendRequest();
              }
            }}
          />
        </Box>
        <br />
        <TextField
          onChange={(event) => setBody(event.target.value)}
          value={body}
          fullWidth
          label='Body'
          variant='filled'
          multiline
          onKeyDown={(event) => event.stopPropagation()}
        />

        <Collapse in={Boolean(response)}>
          <br />

          <Accordion
            elevation={0}
            classes={{
              root: classes.dense,
              expanded: classes.dense,
            }}
          >
            <AccordionSummary
              id='advance-dropdown-summary'
              classes={{
                root: classes.expansionPanelSummary,
                expanded: classes.dense,
              }}
              expandIcon={<ExpandMoreIcon style={{ float: 'left' }} />}
            >
              Response status: {response?.status}
            </AccordionSummary>

            <AccordionDetails className={classes.noPadding}>
              <Box display='flex' flexWrap='wrap'>
                {response?.headers &&
                  Object.entries(response?.headers).map(([key, value]) => (
                    <Chip key={key} size='small' variant='outlined' label={`${key}: ${value}`} style={{ margin: 4 }} />
                  ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          <TextField
            InputLabelProps={{ shrink: Boolean(response?.data) }}
            inputProps={{ readOnly: true }}
            value={response?.data ? JSON.stringify(response?.data, null, 2) : ''}
            fullWidth
            label='Response Body'
            variant='filled'
            multiline
          />
        </Collapse>
      </DialogContent>

      <DialogActions>
        <Button
          id='abort-request'
          disabled={!intervalId}
          color='error'
          onClick={(event) => {
            event.stopPropagation();
            clearInterval(intervalId);
            setIntervalId(undefined);
            setCurrent(undefined);
          }}
        >
          Cancel {typeof count === 'number' && typeof current === 'number' && count - current}
        </Button>
        <TextField
          disabled={Boolean(intervalId)}
          label='delay'
          value={pause}
          onChange={(event) => setPause(Number(event.target.value))}
          type='number'
          InputProps={{
            inputProps: { min: 0 },
            endAdornment: <InputAdornment position='end'>ms</InputAdornment>,
          }}
        />
        <TextField
          disabled={Boolean(intervalId)}
          label='repeat'
          value={count}
          onChange={(event) => setCount(Number(event.target.value))}
          type='number'
          InputProps={{
            inputProps: { min: 1 },
            endAdornment: <InputAdornment position='end'>time(s)</InputAdornment>,
          }}
        />
        <Button
          id='send-request'
          disabled={Boolean(intervalId)}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            sendRequest();
          }}
        >
          Send
        </Button>
      </DialogActions>
    </StdDialog>
  );
};

RequestDialog.propTypes = {
  method: PropTypes.string,
  url: PropTypes.string,
  path: PropTypes.string,
  query: PropTypes.object,
  body: PropTypes.string,
  open: PropTypes.bool,
  onHandleClose: PropTypes.func,
  repeat: PropTypes.number,
  delay: PropTypes.number,
};

export default RequestDialog;
